import { useState, useEffect, forwardRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Loader2, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

interface Props {
  nurseryId: string;
}

const ParentMessaging = forwardRef<HTMLDivElement, Props>(({ nurseryId }, ref) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<any[]>([]);
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminId, setAdminId] = useState<string | null>(null);

  useEffect(() => {
    if (user && nurseryId) {
      fetchMessages();
      fetchAdmin();
    }
  }, [user, nurseryId]);

  const fetchAdmin = async () => {
    const { data } = await supabase
      .from("nurseries")
      .select("owner_id")
      .eq("id", nurseryId)
      .single();
    if (data) setAdminId(data.owner_id);
  };

  const fetchMessages = async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("messages")
      .select("*")
      .eq("nursery_id", nurseryId)
      .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
      .order("created_at", { ascending: true })
      .limit(100);
    setMessages(data || []);
    setLoading(false);
  };

  const handleSend = async () => {
    if (!content.trim() || !user || !adminId) return;
    setSending(true);

    const { error } = await supabase.from("messages").insert({
      content: content.trim(),
      sender_id: user.id,
      receiver_id: adminId,
      nursery_id: nurseryId,
    });

    if (error) {
      toast({ title: "خطأ في الإرسال", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم إرسال الرسالة" });
      setContent("");
      fetchMessages();
    }
    setSending(false);
  };

  return (
    <Card className="shadow-card" ref={ref}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquare className="w-5 h-5 text-primary" />
          مراسلة إدارة الحضانة
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : messages.length === 0 ? (
          <div className="py-8 text-center">
            <MessageSquare className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-muted-foreground text-sm">لا توجد رسائل بعد. أرسل رسالتك الأولى!</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[300px]">
            <div className="space-y-3 pl-1">
              {messages.map((msg) => {
                const isMine = msg.sender_id === user?.id;
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isMine ? "justify-start" : "justify-end"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${
                        isMine
                          ? "bg-primary text-primary-foreground rounded-br-sm"
                          : "bg-muted text-foreground rounded-bl-sm"
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${isMine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        {format(new Date(msg.created_at), "dd MMM HH:mm", { locale: ar })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}

        <div className="flex gap-2">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="اكتب رسالتك للإدارة..."
            rows={2}
            maxLength={1000}
            className="resize-none"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button
            onClick={handleSend}
            disabled={sending || !content.trim()}
            size="icon"
            className="shrink-0 self-end h-10 w-10"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground text-left">{content.length}/1000</p>
      </CardContent>
    </Card>
  );
});

ParentMessaging.displayName = "ParentMessaging";
export default ParentMessaging;
